import AdminTenant, { StoreMgmtStates } from 'models/adminTenant';
import AdminTenantDTO from 'models/dto/adminTenantDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { IdentityProviderType } from 'models/identityProvider';
import {
  fromApiDateTimeOptional,
  fromApiDateTimeRequired,
  toApiDateTimeOptional,
  toApiDateTimeRequired,
} from 'utils/datetime';
import { mapFromDashboard } from './dashboardMapping';
import { mapFromNorms } from './normMapping';
import { mapFromPackage } from './packageMapping';
import { mapFromOnboardingState, mapToOnboardingState } from './tenantMapping';

export function mapFromAdminTenants(tenantDTOs: AdminTenantDTO[], cache: GlobalDataCache): AdminTenant[] {
  return tenantDTOs.map((t) => mapFromAdminTenant(t, cache));
}

export function mapFromAdminTenant(tenantDTO: AdminTenantDTO, cache: GlobalDataCache): AdminTenant {
  const newTenant = new AdminTenant();
  newTenant.tenantId = tenantDTO.tenantId;
  newTenant.parentId = tenantDTO.parentId;
  newTenant.topLevelParentId = tenantDTO.topLevelParentId;
  newTenant.created = fromApiDateTimeRequired(tenantDTO.created);
  newTenant.name = tenantDTO.name;
  newTenant.enableEventSubscriptions = tenantDTO.enableEventSubscriptions;
  newTenant.suspended = tenantDTO.suspended;
  newTenant.licenses = tenantDTO.licenses;
  newTenant.mgmtLicenses = tenantDTO.mgmtLicenses;
  newTenant.trialStart = fromApiDateTimeOptional(tenantDTO.trialStart);
  newTenant.storeMgmtState = tenantDTO.storeMgmtState || StoreMgmtStates.Customer;
  newTenant.onboardingState = mapFromOnboardingState(tenantDTO.onboardingState);
  newTenant.package = mapFromPackage(tenantDTO.package);
  newTenant.usedLicenses = tenantDTO.usedLicenses;
  newTenant.usedMgmtLicenses = tenantDTO.usedMgmtLicenses;
  newTenant.subscriptionType = tenantDTO.subscriptionType;
  newTenant.identityProviderType = tenantDTO.identityProviderType ?? IdentityProviderType.Entra;
  newTenant.providerTenantId = tenantDTO.providerTenantId;
  newTenant.leadSource = tenantDTO.leadSource;
  newTenant.partnerId = tenantDTO.partnerId;
  newTenant.isPartner = tenantDTO.isPartner;
  newTenant.dashboardId = tenantDTO.dashboardId;
  newTenant.norms = mapFromNorms(tenantDTO.norms ?? []);
  newTenant.subscriptionEnd = fromApiDateTimeOptional(tenantDTO.subscriptionEnd);
  newTenant.stripeCustomerId = tenantDTO.stripeCustomerId;
  newTenant.dashboard = tenantDTO.dashboard ? mapFromDashboard(tenantDTO.dashboard, cache) : undefined;

  return newTenant;
}

export function mapToAdminTenant(tenant: AdminTenant): AdminTenantDTO {
  const newTenantDto = new AdminTenantDTO();
  newTenantDto.tenantId = tenant.tenantId;
  newTenantDto.created = toApiDateTimeRequired(tenant.created);
  newTenantDto.name = tenant.name;
  newTenantDto.enableEventSubscriptions = tenant.enableEventSubscriptions;
  newTenantDto.suspended = tenant.suspended;
  newTenantDto.licenses = tenant.licenses;
  newTenantDto.mgmtLicenses = tenant.mgmtLicenses;
  newTenantDto.trialStart = toApiDateTimeOptional(tenant.trialStart);
  newTenantDto.storeMgmtState = tenant.storeMgmtState;
  newTenantDto.onboardingState = mapToOnboardingState(tenant.onboardingState);
  newTenantDto.subscriptionType = tenant.subscriptionType;
  newTenantDto.identityProviderType = tenant.identityProviderType;
  newTenantDto.providerTenantId = tenant.providerTenantId;
  newTenantDto.leadSource = tenant.leadSource;
  newTenantDto.partnerId = tenant.partnerId;
  newTenantDto.isPartner = tenant.isPartner;
  newTenantDto.stripeCustomerId = tenant.stripeCustomerId;
  newTenantDto.subscriptionEnd = toApiDateTimeOptional(tenant.subscriptionEnd);

  return newTenantDto;
}
